import { QueryKeys } from "@/constants";
import { queryClient } from "@/hooks/use-query-client";
import useAuth from "@/context/auth-provider";
import type { Demographics } from "@/model/demographics";
import NotificationToggle from "@/components/csr-chat/notification-toggle";
import { useChatInfo } from "@/hooks/use-csr-chat";
import useCsrTeam from "@/hooks/use-csr-team";
import { isOfflineNow } from "@/lib/utils";
import type { ChatLiabilityData, ClientDetails } from "@/model/csr-chat";
import ProfileImage from "./ProfileImage";

const isValidDetails = (
  value: unknown,
): value is ChatLiabilityData & { client_details: ClientDetails } => {
  if (!value || typeof value !== "object") return false;
  const d = value as Partial<ChatLiabilityData>;
  const cd = d.client_details as Partial<ClientDetails> | undefined;
  return (
    typeof cd?.from_hour === "string" &&
    typeof cd?.to_hour === "string" &&
    typeof cd?.time_zone === "string"
  );
};

const ChatHeader = () => {
  const { authData } = useAuth();
  const demographicsData = queryClient.getQueryData([
    QueryKeys.DEMOGRAPHICS,
    authData!.user!.username,
  ]) as Demographics;
  const { csrTeamName, csrInitials } = useCsrTeam();
  const { data: chatInfo } = useChatInfo();

  const hasDetails = isValidDetails(chatInfo);
  const offline = hasDetails ? isOfflineNow(chatInfo.client_details) : null;
  const isOnline = offline === false;
  const isOffline = offline === true;

  // const hoursLabel = hasDetails
  //   ? `${formatHour(chatInfo.client_details.from_hour)} – ${formatHour(chatInfo.client_details.to_hour)} ${getTimezoneAbbr(chatInfo.client_details.time_zone)}`
  //   : null;

  return (
    <div className="flex items-center justify-start gap-2">
      <div className="relative shrink-0">
        <ProfileImage
          imgUrl={demographicsData?.photoUrl}
          nameLetters={csrInitials}
        />
        {isOnline && (
          <span className="absolute bottom-0 right-0 flex size-3">
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex size-3 rounded-full bg-green-500 ring-2 ring-white" />
          </span>
        )}
        {isOffline && (
          <span className="absolute bottom-0 right-0 size-3 rounded-full bg-gray-400 ring-2 ring-white" />
        )}
      </div>

      <div className="flex flex-col justify-center">
        <h3 className="text-base font-semibold leading-tight">{csrTeamName}</h3>
        {isOnline && (
          <span className="text-xs font-medium leading-tight text-green-600">
            Online
          </span>
        )}
        {isOffline && (
          <span className="text-xs leading-tight text-muted-foreground">
            Offline
          </span>
        )}
        {!hasDetails && (
          <span className="text-xs leading-tight text-muted-foreground">
            CSR Support
          </span>
        )}
      </div>

      <NotificationToggle />
    </div>
  );
};

export default ChatHeader;
